import { useCallback, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useT, Namespace } from "@racoon/i18n";
import {
  Button,
  type Column,
  DataTable,
  FilterBlock,
  type FilterStateItem,
  useModal,
} from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { matchesSearch } from "@racoon/lib";
import {
  useCreateCustomerTypeMutation,
  useCreatePickupLocationTypeMutation,
  useListCustomerTypesQuery,
  useListPickupLocationTypesQuery,
  useUpdateCustomerTypeMutation,
  useUpdatePickupLocationTypeMutation,
} from "~/shared/api/organization-labels/organization-labels.grpc";
import { useListCounterpartiesQuery } from "~/shared/api/organization/organization.grpc";
import { useListPickupLocationsQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { OrganizationLabelForm } from "./organization-label-form";
import type {
  OrganizationLabelKind,
  OrganizationLabelMutations,
} from "./organization-label.schema";

type Counts = Record<string, number> | undefined;

/**
 * Each kind's endpoints. The counts are the entity list's own id facet, so each matches
 * what that label's filter lists; page size 1, only list_info is needed.
 */
const KINDS: Record<
  OrganizationLabelKind,
  {
    useList: typeof useListPickupLocationTypesQuery;
    useCreate: typeof useCreatePickupLocationTypeMutation;
    useUpdate: typeof useUpdatePickupLocationTypeMutation;
    useCounts: (org: bigint | undefined) => Counts;
  }
> = {
  pickupLocationTypes: {
    useList: useListPickupLocationTypesQuery,
    useCreate: useCreatePickupLocationTypeMutation,
    useUpdate: useUpdatePickupLocationTypeMutation,
    useCounts: (org) =>
      useListPickupLocationsQuery(
        {
          serviceProviderIds: org ? [org] : [],
          listFilter: { pageSize: 1, facets: ["pickup_location_type_id"] },
        },
        {
          skip: !org,
          selectFromResult: ({ currentData }) => ({
            counts: currentData?.listInfo?.facets["pickup_location_type_id"]?.idCounts,
          }),
        },
      ).counts,
  },
  customerTypes: {
    useList: useListCustomerTypesQuery,
    useCreate: useCreateCustomerTypeMutation,
    useUpdate: useUpdateCustomerTypeMutation,
    useCounts: (org) =>
      useListCounterpartiesQuery(
        { organizationId: org ?? 0n, listFilter: { pageSize: 1, facets: ["customer_type_id"] } },
        {
          skip: !org,
          selectFromResult: ({ currentData }) => ({
            counts: currentData?.listInfo?.facets["customer_type_id"]?.idCounts,
          }),
        },
      ).counts,
  },
};

/** Entities with label `id` ("0": with none); undefined while the counts load. */
const countOf = (counts: Counts, id: string) => (counts ? (counts[id] ?? 0) : undefined);

type Tab = "active" | "archived";

interface Row {
  key: string;
  name: string;
  count?: number;
  /** null: the «Не указано» row, entities with no label. */
  label: ConnectProto.OrganizationLabel | null;
}

interface OrganizationLabelsSettingsProps {
  kind: OrganizationLabelKind;
  selectedCompanyId?: number;
}

/** One settings tab over a kind of organization label: pickup location or customer types. */
export const OrganizationLabelsSettings = ({
  kind,
  selectedCompanyId,
}: OrganizationLabelsSettingsProps) => {
  const t = useT();
  const { openModal, closeModal } = useModal();
  const [tab, setTab] = useState<Tab>("active");
  const [search, setSearch] = useState("");

  const org = selectedCompanyId ? BigInt(selectedCompanyId) : undefined;
  const { useList, useCreate, useUpdate, useCounts } = KINDS[kind];
  // `currentData`: after an org switch `data` still holds the previous org's labels.
  const { currentData, isFetching } = useList(
    { ownerOrganizationIds: org ? [org] : [] },
    { skip: !org },
  );
  const counts = useCounts(org);
  const [create] = useCreate();
  const [update] = useUpdate();
  const mutations = useMemo<OrganizationLabelMutations>(
    () => ({
      create: (name) => create({ ownerOrganizationId: org ?? 0n, name }).unwrap(),
      update: (id, change) => update({ id, ownerOrganizationId: org ?? 0n, ...change }).unwrap(),
    }),
    [create, update, org],
  );

  const { active, archived } = useMemo(() => {
    const labels = currentData?.labels ?? [];
    const isArchived = (l: ConnectProto.OrganizationLabel) =>
      l.status === ConnectProto.LocationStatus.INACTIVE;
    return { active: labels.filter((l) => !isArchived(l)), archived: labels.filter(isArchived) };
  }, [currentData]);

  const rows = useMemo<Row[]>(() => {
    const toRow = (label: ConnectProto.OrganizationLabel): Row => ({
      key: label.id.toString(),
      name: label.name,
      count: countOf(counts, label.id.toString()),
      label,
    });
    const all =
      tab === "active"
        ? [
            {
              key: "none",
              name: t(Namespace.Settings, "organizationLabels.undefined"),
              count: countOf(counts, "0"),
              label: null,
            },
            ...active.map(toRow),
          ]
        : archived.map(toRow);
    return all.filter((row) => matchesSearch(row.name, search));
  }, [tab, active, archived, counts, search, t]);

  const openLabelModal = useCallback(
    (label: ConnectProto.OrganizationLabel | null) => {
      const modalId = openModal({
        title: label
          ? t(Namespace.Settings, `${kind}.dialog.editTitle`)
          : t(Namespace.Settings, `${kind}.dialog.createTitle`),
        width: "30rem",
        bodyClassName: "!p-0",
        body: (
          <OrganizationLabelForm
            label={label}
            mutations={mutations}
            onClose={() => closeModal(modalId)}
          />
        ),
      });
    },
    [t, kind, mutations, openModal, closeModal],
  );

  const columns = useMemo<Column<Row>[]>(
    () => [
      { id: "index", header: "#", accessor: (_row, i) => i + 1, width: 77 },
      {
        id: "name",
        header: t(Namespace.Settings, `${kind}.table.name`),
        accessor: (row) => row.name,
        sortable: true,
        width: 414,
      },
      {
        id: "count",
        header: t(Namespace.Settings, `${kind}.table.count`),
        accessor: (row) => row.count ?? "—",
        sortable: true,
      },
      {
        id: "action",
        header: t(Namespace.Settings, "organizationLabels.table.action"),
        accessor: () => "",
        cell: ({ row }) =>
          row.original.label && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="!text-sm font-medium text-blue-600 hover:text-blue-700"
              onClick={() => openLabelModal(row.original.label)}
            >
              {t(Namespace.Settings, "organizationLabels.edit")}
            </Button>
          ),
      },
    ],
    [t, kind, openLabelModal],
  );

  const states = useMemo<FilterStateItem[]>(
    () => [
      {
        key: t(Namespace.Settings, "organizationLabels.status.active"),
        value: "active",
        count: active.length,
      },
      {
        key: t(Namespace.Settings, "organizationLabels.status.archived"),
        value: "archived",
        count: archived.length,
      },
    ],
    [t, active.length, archived.length],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <FilterBlock
        direction="row"
        bgColor="white"
        statesVariant="tab"
        states={states}
        selectedState={states.find((s) => s.value === tab)}
        onStateChange={(state) => setTab(state.value as Tab)}
        searchValue={search}
        onSearchChange={setSearch}
        justifyBetween
        additionalButtons={
          <Button
            size="sm"
            variant="ghost"
            className="gap-1 !bg-blue-100 font-medium text-blue-600 hover:!bg-blue-200"
            onClick={() => openLabelModal(null)}
          >
            <Plus className="size-4" />
            {t(Namespace.Settings, `${kind}.addButton`)}
          </Button>
        }
      />

      <div className="min-h-0 flex-1 rounded-lg">
        <DataTable
          className="h-full"
          columns={columns}
          data={rows}
          loading={isFetching}
          rowKey={(row) => row.key}
          namespace={Namespace.Settings}
          empty={
            <p className="p-8 text-center text-text-subtle">
              {t(Namespace.Settings, `${kind}.table.empty`)}
            </p>
          }
        />
      </div>
    </div>
  );
};
